import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  NgZone,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { Chart, ChartConfiguration } from 'chart.js/auto';

@Component({
  selector: 'app-bar-chart',
  standalone: true,
  imports: [],
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.css'],
})
export class BarChartComponent implements OnChanges, AfterViewInit {
  @Input({ required: true }) chartId: string = 'bar-chart';
  @Input() showxAxesLabels: boolean = true;
  @Input() showGrid: boolean = true;
  @Input() showLegend: boolean = true;
  @Input() aspectRatio: boolean = true;
  @Input() dualGraph: boolean = false;
  @Input() isInModal: boolean = false;
  @Input() tooltipWrap: boolean = true;
  @Input() xAxesLabels: string[] = [];
  @Input() barLabel: string = 'bar label';
  @Input() barData: number[] = [];
  @Input() barBackgroundColors: string[] = [];
  @Input() bar2Label: string = 'bar2 label';
  @Input() bar2Data: number[] = [];
  @Input() bar2BackgroundColors: string[] = [];
  @Input() barRadius: number = 0;
  @Input() legendFontSize: number = 14;
  @Input() xTicksFontSize: number = 14;
  @Input() yTicksFontSize: number = 14;
  @Input() legendDotSize: number = 20;
  @Input() barLegendText: string = '';
  @Input() bar2LegendText: string = '';
  @Input() legendTextColor: string = '#333';
  @Input() legendAlign: 'start' | 'end' | 'center' = 'center';
  @Input() legendPosition:
    | 'center'
    | 'left'
    | 'right'
    | 'bottom'
    | 'top'
    | 'chartArea' = 'top';

  @Input() tooltipCurrentLabels: string[] | null = null;
  @Input() tooltipPreviousLabels: string[] | null = null;
  chart!: Chart;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const tooltipEl = document.getElementById(
      'tooltip-' + this.chartId,
    ) as HTMLDivElement;

    const config: ChartConfiguration = {
      type: 'bar',
      data: {
        labels: this.xAxesLabels,
        datasets: [
          {
            label: this.barLabel,
            type: 'bar',
            data: this.barData,
            backgroundColor: this.barBackgroundColors,
            borderRadius: this.barRadius,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: this.aspectRatio,
        plugins: {
          legend: {
            display: this.showLegend,
            position: this.legendPosition,
            align: this.legendAlign,
            labels: {
              generateLabels: (chart) => {
                const defaultLabels =
                  Chart.defaults.plugins.legend.labels.generateLabels(chart);
                const customTexts = [this.barLegendText, this.bar2LegendText];

                return defaultLabels.map((label, index) => ({
                  ...label,
                  text: customTexts[index] || label.text,
                }));
              },
              boxWidth: this.legendDotSize,
              boxHeight: this.legendDotSize,
              borderRadius: this.legendDotSize / 2,
              font: { size: this.legendFontSize },
              color: this.legendTextColor,
              useBorderRadius: true,
            },
          },
          tooltip: {
            enabled: false,
            external: (context) => {
              const { chart, tooltip } = context;
              if (!tooltip || tooltip.opacity === 0) {
                tooltipEl.style.opacity = '0';
                return;
              }

              const dp = tooltip.dataPoints?.[0];
              if (!dp) return;
              const dataIndex = dp.dataIndex;
              const datasetIndex = dp.datasetIndex;
              const datasetLabel = chart.data.datasets[datasetIndex].label;
              const hasCustom =
                Array.isArray(this.tooltipCurrentLabels) &&
                Array.isArray(this.tooltipPreviousLabels);
              let html = '';

              if (hasCustom) {
                const title =
                  datasetIndex === 0
                    ? this.tooltipPreviousLabels![dataIndex] ||
                      this.tooltipCurrentLabels![dataIndex]
                    : this.tooltipCurrentLabels![dataIndex];

                html = `${title}<br>${datasetLabel}: ${dp.formattedValue}`;
              } else {
                const fallbackTitle = this.xAxesLabels[dataIndex] ?? '';
                html = `${fallbackTitle}<br>${datasetLabel}: ${dp.formattedValue}`;
              }

              tooltipEl.innerHTML = html;
              tooltipEl.style.opacity = '1';
              if (!this.tooltipWrap) tooltipEl.style.textWrap = 'nowrap';

              const rect = chart.canvas.getBoundingClientRect();

              if (this.isInModal) {
                tooltipEl.style.position = 'fixed';
                tooltipEl.style.left = `${rect.left + tooltip.caretX}px`;
                tooltipEl.style.top = `${rect.top + tooltip.caretY - tooltip.height - 10}px`;
              } else {
                tooltipEl.style.left =
                  rect.left + window.scrollX + tooltip.caretX + 'px';
                tooltipEl.style.top =
                  rect.top +
                  window.scrollY +
                  tooltip.caretY -
                  tooltip.height +
                  'px';
              }
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              display: this.showxAxesLabels,
              font: { size: this.yTicksFontSize },
            },
            grid: { display: this.showGrid },
          },
          x: {
            ticks: {
              display: this.showxAxesLabels,
              font: { size: this.xTicksFontSize },
            },
            grid: { display: this.showGrid },
          },
        },
      },
    };

    if (this.dualGraph) {
      config.data.datasets.push({
        label: this.bar2Label,
        type: 'bar',
        data: this.bar2Data,
        backgroundColor: this.bar2BackgroundColors,
        borderRadius: this.barRadius,
      });
    }

    this.zone.run(() => {
      this.chart = new Chart(`chart-${this.chartId}`, config);
    });
  }

  ngOnChanges(): void {
    if (!this.chart) return;
    this.chart.data.labels = this.xAxesLabels;
    if (this.chart.data.datasets[0]) {
      this.chart.data.datasets[0].label = this.barLabel;
      this.chart.data.datasets[0].data = [...this.barData];
    }
    if (this.dualGraph) {
      if (this.chart.data.datasets[1]) {
        this.chart.data.datasets[1].label = this.bar2Label;
        this.chart.data.datasets[1].data = [...this.bar2Data];
      } else {
        this.chart.data.datasets.push({
          label: this.bar2Label,
          type: 'bar',
          data: [...this.bar2Data],
          backgroundColor: this.bar2BackgroundColors,
          borderRadius: this.barRadius,
        });
      }
    }
    this.chart.update();
  }
}
